import { isEmpty } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Total } from 'core/elements';
import { useAgGridClientTableContext } from 'core/states';
import { IPartInventoryItem } from 'parts/types';

import { getLocationKey } from './utils';

const STATUS_PANEL_CLASS = 'ag-status-name-value ag-status-panel';

export const LocationsCountStatusBar = () => {
  const { t } = useTranslation();

  const { gridApi, rows } = useAgGridClientTableContext();

  const totalLocationsCount = useMemo(() => {
    const records = rows as IPartInventoryItem[];

    return new Set(records?.map(getLocationKey)).size;
  }, [rows]);

  const [filteredLocationsCount, setFilteredLocationsCount] = useState<number | null>(null);

  useEffect(() => {
    if (!gridApi || gridApi.isDestroyed()) {
      return;
    }

    const recompute = () => {
      const hasActiveFilter = !isEmpty(gridApi.getFilterModel());

      if (!hasActiveFilter) {
        setFilteredLocationsCount(null);

        return;
      }

      const locations = new Set<string>();

      gridApi.forEachNodeAfterFilter((node) => {
        if (node.data) {
          locations.add(getLocationKey(node.data as IPartInventoryItem));
        }
      });

      setFilteredLocationsCount(locations.size);
    };

    recompute();

    gridApi.addEventListener('filterChanged', recompute);

    return () => {
      if (!gridApi.isDestroyed()) {
        gridApi.removeEventListener('filterChanged', recompute);
      }
    };
  }, [gridApi]);

  return (
    <>
      <Total
        label={t('labelTotalWithType', { entityType: t('labelInventoryItems') })}
        value={totalLocationsCount}
        options={{ classNames: STATUS_PANEL_CLASS }}
      />
      {filteredLocationsCount !== null && (
        <Total
          label={t('labelFiltered')}
          value={filteredLocationsCount}
          options={{ classNames: STATUS_PANEL_CLASS }}
        />
      )}
    </>
  );
};
